import {Formik} from "formik";
import {Button, Modal} from "react-bootstrap";
import {useUpdateTechnician} from "../../../hooks/useUpdateUser";
import BancAvatarIcon from "../../../images/icons/bancAvatar";
import GooglePlacesAutocomplete from "../shared/SafeGooglePlacesAutocomplete";
import {useState} from "react";
import * as yup from "yup";
import {bancApi} from "../../../services/technicianService";
import {showToastModal} from "../shared/ToastModalSwal";
import googleMapsApiKey from "../../../config/googleMaps";

const BANK_VERIFICATION_PENDING_KEY = "stripe_bank_verification_pending";

// Regex for IBAN validation (basic format check)
const ibanRegex = /^[A-Za-z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;

// Regex for RIB validation (French format: Bank Code (5) + Branch Code (5) + Account Number (11) + RIB Key (2))
const ribRegex = /^\d{5}\d{5}\d{11}\d{2}$/;
const validationSchema = yup.object().shape({
  bankName: yup.string().min(2).required("champ obligatoire"),
  iban: yup
    .string()
    .required("champ obligatoire")
    .test("iban-valid", "Invalid form IBAN ou RIB ", (value) => {
      if (!value) return false;
      return ibanRegex.test(value) || ribRegex.test(value);
    }),
  bic: yup
    .string()
    .required("champ obligatoire")
    .matches(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, "Invalid BIC format"),
  address: yup.string().notRequired().nullable(),
});

export const AddBancModal = ({show, setShow}) => {
  const {onAddBanc} = useUpdateTechnician();
  const [selectedAddress, setSelectedAddress] = useState(null);

  const handlePlaceSelect = async (address, setFieldValue) => {
    setFieldValue("address", address.label);
    setSelectedAddress(address);
  };

  return (
    <Modal
      className="modal fade"
      centered
      show={show}
      onHide={() => {
        setShow(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title !font-bold">
            {" "}
            Ajouter un compte bancaire{" "}
          </h5>
          <Button
            variant=""
            type="button"
            className="close"
            data-dismiss="modal"
            onClick={() => {
              setShow(false);
            }}
          >
            <span>×</span>
          </Button>
        </div>
        <div className="modal-body">
          <Formik
            enableReinitialize
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={true}
            initialValues={{
              bankName: "",
              iban: "",
              bic: "",
              address: "",
            }}
            onSubmit={(values, {setSubmitting}) => {
              onAddBanc({...values, isSelected: true}, {
                onSuccess: async () => {
                  try {
                    const onboarding = await bancApi.startConnectOnboarding(
                      "bank_account_update",
                    );
                    if (!onboarding?.url) {
                      throw new Error("Stripe n'a pas renvoyé de lien de vérification.");
                    }
                    sessionStorage.setItem(BANK_VERIFICATION_PENDING_KEY, "1");
                    window.location.assign(onboarding.url);
                  } catch (error) {
                    sessionStorage.removeItem(BANK_VERIFICATION_PENDING_KEY);
                    setSubmitting(false);
                    setShow(false);
                    showToastModal({
                      success: false,
                      title: "Vérification Stripe impossible",
                      message:
                        error?.response?.data?.error
                        || "Impossible d'ouvrir la vérification Stripe. Veuillez réessayer.",
                    });
                  }
                },
                onError: () => setSubmitting(false),
              });
            }}
          >
            {({
              values,
              errors,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              resetForm,
              setFieldValue,
              touched,
            }) => (
              <form
                className="comment-form"
                onSubmit={
                  handleSubmit
                  // dispatch({type: "sendMessage"});
                }
              >
                <div className="row">
                  <div className="col-lg-12">
                    <div className="form-group mb-3">
                      <label
                        htmlFor="bankName"
                        className="text-[#3D4465] font-normal leading-3 text-sm"
                      >
                        {" "}
                        Banque <span className="required">*</span>{" "}
                      </label>
                      <input
                        type="text"
                        className="form-control uppercase"
                        name="bankName"
                        value={values.bankName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors.bankName && touched.bankName && (
                        <div
                          className="invalid-feedback animated fadeInUp"
                          style={{display: "block"}}
                        >
                          {errors.bankName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group mb-3">
                      <label
                        htmlFor="iban"
                        className="text-[#3D4465] font-normal leading-3 text-sm"
                      >
                        {" "}
                        IBAN / RIB <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control uppercase"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.iban}
                        name="iban"
                      />
                      {errors.iban && touched.iban && (
                        <div
                          className="invalid-feedback animated fadeInUp"
                          style={{display: "block"}}
                        >
                          {errors.iban}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group mb-3">
                      <label
                        htmlFor="iban"
                        className="text-[#3D4465] font-normal leading-3 text-sm"
                      >
                        BIC <span className="required">*</span>
                      </label>
                      <input
                        className="form-control uppercase"
                        name="bic"
                        value={values.bic}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors?.bic && touched?.bic && (
                        <div
                          className="invalid-feedback animated fadeInUp"
                          style={{display: "block"}}
                        >
                          {errors?.bic}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group w-full col-span-2 ">
                      <label className="text-[#3D4465] font-normal leading-3 text-sm">
                        Adresse de la banque
                      </label>
                      <GooglePlacesAutocomplete
                        apiKey={googleMapsApiKey}
                        selectProps={{
                          value: selectedAddress,

                          onChange: (address) =>
                            handlePlaceSelect(address, setFieldValue),
                          placeholder: "Recherchez une adresse...",
                          styles: {
                            dropdownIndicator: (base) => ({
                              ...base,
                              display: "none", // Hides the dropdown icon
                            }),
                            indicatorSeparator: (base) => ({
                              ...base,
                              display: "none", // Hides the separator line
                            }),
                            control: (base) => ({
                              ...base,
                              borderColor: "#eee", // Matches the other inputs' border color
                              borderWidth: "1px",
                              borderRadius: "0.375rem", // Rounded corners like your other inputs
                              padding: "0.375rem 0.75rem", // Add padding to match the input
                              boxShadow: "none",
                            }),
                            input: (base) => ({
                              ...base,
                              fontSize: "0.875rem", // Matches your other inputs' font size
                              color: "#374151", // Text color
                            }),
                            placeholder: (base) => ({
                              ...base,
                              color: "#9ca3af", // Placeholder color
                              fontSize: "0.875rem",
                            }),
                          },
                        }}
                      />
                      {errors.address && (
                        <div
                          className="invalid-feedback animated fadeInUp"
                          style={{display: "block"}}
                        >
                          {errors.address}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group mt-3">
                      <button
                        disabled={isSubmitting}
                        type="submit"
                        className="!rounded-md w-full !px-3 !py-3 text-white bg-gradient-to-r from-[#0022AA] to-[#1A64B9] font-medium text-sm    uppercase"
                      >
                        Vérifier votre compte
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
  );
};

export const UpdateBancModal = ({show, setShow, banc}) => {
  const {onUpdateBanc} = useUpdateTechnician();
  const [selectedAddress, setSelectedAddress] = useState(null);

  const handlePlaceSelect = async (address, setFieldValue) => {
    setFieldValue("address", address.label);
    setSelectedAddress(address);
  };
  return (
    <Modal
      className="modal fade"
      show={show}
      centered
      onHide={() => {
        setShow(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title !font-bold">
            {" "}
            Modifier un compte bancaire
          </h5>
          <Button
            variant=""
            type="button"
            className="close"
            data-dismiss="modal"
            onClick={() => {
              setShow(false);
            }}
          >
            <span>×</span>
          </Button>
        </div>
        <div className="modal-body">
          <Formik
            enableReinitialize
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={true}
            initialValues={{
              ...banc,
            }}
            onSubmit={(values, {setSubmitting}) => {
              onUpdateBanc({body: {...values}, id: banc.id});
              setSubmitting(false);
              setShow(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              resetForm,
              setFieldValue,
            }) => (
              <form
                className="comment-form"
                onSubmit={
                  handleSubmit
                  // dispatch({type: "sendMessage"});
                }
              >
                <div className="row">
                  <div className="col-lg-12">
                    <div className="form-group mb-3">
                      <label
                        htmlFor="bankName"
                        className="text-[#3D4465] font-normal leading-3 text-sm"
                      >
                        {" "}
                        Banque <span className="required">*</span>{" "}
                      </label>
                      <input
                        type="text"
                        className="form-control uppercase"
                        name="bankName"
                        value={values.bankName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors.bankName && touched.bankName && (
                        <div
                          className="invalid-feedback animated fadeInUp"
                          style={{display: "block"}}
                        >
                          {errors.bankName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group mb-3">
                      <label
                        htmlFor="iban"
                        className="text-[#3D4465] font-normal leading-3 text-sm"
                      >
                        {" "}
                        IBAN / RIB <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control uppercase"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.iban}
                        name="iban"
                      />
                      {errors.iban && touched.iban && (
                        <div
                          className="invalid-feedback animated fadeInUp"
                          style={{display: "block"}}
                        >
                          {errors.iban}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group mb-3">
                      <label
                        htmlFor="iban"
                        className="text-[#3D4465] font-normal leading-3 text-sm"
                      >
                        BIC <span className="required">*</span>
                      </label>
                      <input
                        className="form-control uppercase"
                        name="bic"
                        value={values.bic}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors?.bic && touched?.bic && (
                        <div
                          className="invalid-feedback animated fadeInUp"
                          style={{display: "block"}}
                        >
                          {errors?.bic}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group w-full col-span-2 ">
                      <label className="text-[#3D4465] font-normal leading-3 text-sm">
                        Adresse de la banque
                      </label>
                      <GooglePlacesAutocomplete
                        apiKey={googleMapsApiKey}
                        selectProps={{
                          value: selectedAddress,
                          defaultInputValue: banc.address,
                          onChange: (address) =>
                            handlePlaceSelect(address, setFieldValue),
                          placeholder: "Recherchez une adresse...",
                          styles: {
                            dropdownIndicator: (base) => ({
                              ...base,
                              display: "none", // Hides the dropdown icon
                            }),
                            indicatorSeparator: (base) => ({
                              ...base,
                              display: "none", // Hides the separator line
                            }),
                            control: (base) => ({
                              ...base,
                              borderColor: "#eee", // Matches the other inputs' border color
                              borderWidth: "1px",
                              borderRadius: "0.375rem", // Rounded corners like your other inputs
                              padding: "0.375rem 0.75rem", // Add padding to match the input
                              boxShadow: "none",
                            }),
                            input: (base) => ({
                              ...base,
                              fontSize: "0.875rem", // Matches your other inputs' font size
                              color: "#374151", // Text color
                            }),
                            placeholder: (base) => ({
                              ...base,
                              color: "#9ca3af", // Placeholder color
                              fontSize: "0.875rem",
                            }),
                          },
                        }}
                      />
                      {errors.address && (
                        <div
                          className="invalid-feedback animated fadeInUp"
                          style={{display: "block"}}
                        >
                          {errors.address}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group mt-3">
                      <button
                        disabled={isSubmitting}
                        type="submit"
                        className="!rounded-md w-full !px-3 !py-3 text-white bg-gradient-to-r from-[#0022AA] to-[#1A64B9] font-medium text-sm    uppercase"
                      >
                        Enregistrer
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
  );
};

export const ViewBancModal = ({show, setShow, banc}) => {
  return (
    <Modal
      className="modal fade"
      show={show}
      centered
      onHide={() => {
        setShow(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title !font-bold"> Détails du compte</h5>
          <Button
            variant=""
            type="button"
            className="close"
            data-dismiss="modal"
            onClick={() => {
              setShow(false);
            }}
          >
            <span>×</span>
          </Button>
        </div>
        <div className="modal-body !px-5 !py-7">
          <div className="flex flex-col items-start gap-2">
            <div className="flex items-center justify-start gap-3">
              <BancAvatarIcon />
              <div className="uppercase font-bold text-lg">
                {banc?.bankName}
              </div>
            </div>
            <div className="uppercase flex gap-2 justify-start w-full">
              <span className="font-semibold  min-w-[70px] ">IBAN:</span>{" "}
              <span className="break-all">{banc?.iban}</span>
            </div>
            <div className="uppercase flex gap-2 justify-start w-full">
              <span className="font-semibold  min-w-[70px] ">Bic:</span>{" "}
              <span className="break-all">{banc?.bic}</span>
            </div>
            <div className="uppercase flex gap-2 justify-start w-full">
              <span className="font-semibold  min-w-[70px] ">Adresse:</span>{" "}
              <span className="text-wrap break-all">
                {banc?.address ?? "--"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
